import * as multiAz from './multi-az';
import {event} from './google-analytics';
import * as helpers from './helpers';
import * as self from './api';
import {useCredentials} from './user';

export function useApi() {
  const credentials = useCredentials();
  
  return Object.keys(self)
    .reduce((obj, key) => {
      obj[key] = (...args) => self[key](...args, credentials);
      return obj;
    }, {});
}

export function getApiUrl() {
  return multiAz.getCurrentZone();
}

export async function login(username, password) {
  const body = JSON.stringify({username, password});
  const request = createRequest('POST', null, body);

  // Attempt to login to every availability zone.
  const zones = multiAz.getAllZones();
  const results = await Promise.all(zones.map(async url => {
    try {
      const response = await fetch(`${url}/authenticate`, request);

      if (!response.ok) {
        return null;
      }

      return response.json();
    }
    catch (e) {
      return null;
    }
  }));

  // Find the availability zone that accepted the credentials.
  const zoneIndex = results.map(response => !!response).indexOf(true);

  // If none of the attempts were successful, the user entered incorrect credentials.
  if (zoneIndex < 0) {
    throw new Error('Invalid credentials');
  }

  // Set the current availability zone and return the user data.
  multiAz.setCurrentZone(zones[zoneIndex]);
  return results[zoneIndex];
}

export async function autoLogin(credentials) {
  const request = createRequest('GET', credentials);

  // Attempt to load the user details from every availability zone to determine the correct one.
  const zones = multiAz.getAllZones();
  const results = await Promise.all(zones.map(async url => {
    const response = await fetch(`${url}/user`, request);

    if (!response.ok) {
      return null;
    }

    return response.json();
  }));

  // Find the availability zone that accepted the credentials.
  const zoneIndex = results.map(response => !!response).indexOf(true);

  // If none of the attempts were successful, the user entered incorrect credentials.
  if (zoneIndex < 0) {
    throw new Error('Unable to auto-login - credentials not recognized');
  }

  // Set the current availability zone and return the user data.
  multiAz.setCurrentZone(zones[zoneIndex]);
  return results[zoneIndex];
}

export async function getCompanyTemplates(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/company/template`, request);

  if (!response.ok) {
    throw response;
  }

  return await response.json();
}

export async function getCompanySheets(page, size, sort, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/company/sheet?page=${page}&size=${size}&sort=${sort}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function filterCompanySheets(templateId, userId, sheetName, page, size, sort, credentials) {
  const request = createRequest('GET', credentials);
  const params = [`page=${page}`, `size=${size}`, `sort=${sort}`];

  if (templateId) {
    params.push(`templateId=${templateId}`);
  }

  if (userId) {
    params.push(`userId=${userId}`);
  }

  if (sheetName) {
    params.push(`sheetName=${sheetName}`);
  }

  const response = await fetch(`${getApiUrl()}/company/sheet?${params.join('&')}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getReports(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/report`, request);

  if(!response.ok) {
    throw response;
  }

  return await response.json();
}

async function runReport(reportId, parameters, format, credentials) {
  const imageFormat = helpers.isIE() ? "PNG" : "SVG";
  const request = createRequest('POST', credentials, JSON.stringify({format, imageFormat, parameters}));
  const response = await fetch(`${getApiUrl()}/report/${reportId}`, request);

  if(!response.ok || response.status === 204) {
    throw response;
  }

  return await response;
}

export async function runHtmlReport(reportId, parameters, credentials) {
  const response = await runReport(reportId, parameters, 'HTML', credentials);
  return await response.text();
}

export async function runPdfReport(reportId, parameters, credentials) {
  const response = await runReport(reportId, parameters, 'PDF', credentials);
  return await response.blob();
}

export async function runXlsxReport(reportId, parameters, credentials) {
  const response = await runReport(reportId, parameters, 'XLSX', credentials);
  return await response.blob();
}

export async function getDesignFiles(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/report/designs`, request);

  if(!response.ok) {
    throw response;
  }

  return await response.json();
}

export async function getCatalog(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/catalog`, request);

  if(!response.ok) {
    throw response;
  }

  return await response.json();
}

export async function getCatalogTypes(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/catalog/types`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createReport(reportId, templateIds, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify({reportId, templateIds}));
  const response = await fetch(`${getApiUrl()}/report`, request);

  if(!response.ok) {
    throw response;
  }

  return await response.json();
}

export async function publishReport(templateReportId, credentials) {
  const request = createRequest('PUT', credentials);
  const response = await fetch(`${getApiUrl()}/report/${templateReportId}/publish`, request);

  if(!response.ok) {
    throw response;
  }
}

export async function getAllUsers(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/company/users`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getCurrentUser(credentials) {
  const response = await fetch(`${getApiUrl()}/user`, createRequest('GET', credentials));

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createUser(user, credentials) {
  const payload = {...user, groups: user.groups.map(group => group.id)};

  const request = createRequest('POST', credentials, JSON.stringify(payload));
  const response = await fetch(`${getApiUrl()}/user`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function updateUser(user, credentials) {
  const payload = {...user, groups: user.groups.map(group => group.id)};

  const request = createRequest('PUT', credentials, JSON.stringify(payload));
  const response = await fetch(`${getApiUrl()}/user/${user.id}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createTemplate(template, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify({
    name: template.name,
    groupIds: template.groups.map(group => group.id),
    betaTestUsers: template.betaTestUsers.map(user => user.id),
    questions: [...template.contextQuestions, ...template.questions],
    elements: template.elements,
    alerts: template.alerts,
    multi: template.multi,
    catalogTypeIds: template.catalogTypes.map(type => type.id),
    rowCompleteContacts: template.rowCompleteContacts.map(user => user.id),
    sheetCompleteContacts: template.sheetCompleteContacts.map(user => user.id),
    rowApprovedContacts: template.rowApprovedContacts.map(user => user.id),
    rowRejectedContacts: template.rowRejectedContacts.map(user => user.id),
    reviewers: template.reviewers.map(user => user.id),
    contextAlerts: template.contextAlerts,
    tags: template.tags || []
  }));
  const response = await fetch(`${getApiUrl()}/template`, request);

  if(!response.ok) {
    throw response;
  }
  event('Template', 'Create', 'New Template Created');

  return response.json();
}

export async function editTemplate(template, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({
    name: template.name,
    groupIds: template.groups.map(group => group.id),
    betaTestUsers: template.betaTestUsers.map(user => user.id),
    questions: [...template.contextQuestions, ...template.questions],
    elements: template.elements,
    alerts: template.alerts,
    multi: template.multi,
    catalogTypeIds: template.catalogTypes.map(type => type.id),
    rowCompleteContacts: template.rowCompleteContacts.map(user => user.id),
    sheetCompleteContacts: template.sheetCompleteContacts.map(user => user.id),
    rowApprovedContacts: template.rowApprovedContacts.map(user => user.id),
    rowRejectedContacts: template.rowRejectedContacts.map(user => user.id),
    reviewers: template.reviewers.map(user => user.id),
    contextAlerts: template.contextAlerts,
    tags: template.tags || []
  }));
  const response = await fetch(`${getApiUrl()}/template/${template.id}`, request);

  if(!response.ok) {
    throw response;
  }
  event('Template', 'Modify', 'Template Modified');

  return response.json();
}

export async function getTemplate(templateId, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/template/${templateId}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function archiveTemplate(templateId, credentials) {
  const request = createRequest('PUT', credentials);
  const response = await fetch(`${getApiUrl()}/template/${templateId}/archive`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function unarchiveTemplate(templateId, credentials) {
  const request = createRequest('PUT', credentials);
  const response = await fetch(`${getApiUrl()}/template/${templateId}/unarchive`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function publishTemplate(templateId, credentials) {
  const request = createRequest('PUT', credentials);
  const response = await fetch(`${getApiUrl()}/template/${templateId}/publish`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function updateQuestion(questionId, question, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(question));
  const response = await fetch(`${getApiUrl()}/question/${questionId}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function reorderQuestions(templateId, questionOrdering, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({questionOrdering}));
  const response = await fetch(`${getApiUrl()}/template/${templateId}/questions/reorder`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getUserGroups(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/usergroup`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createUserGroup(group, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify({
    ...group,
    members: group.members.map(user => user.id)
  }));
  const response = await fetch(`${getApiUrl()}/usergroup`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function updateUserGroup(group, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({
    ...group,
    members: group.members.map(user => user.id)
  }));
  const response = await fetch(`${getApiUrl()}/usergroup/${group.id}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteUserGroup(group, credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/usergroup/${group.id}`, request);

  if(!response.ok) {
    throw response;
  }
}

async function exportTemplateData(templateIds, startDate, endDate, credentials) {
  const oneDay = 1000*60*60*24;
  const finalDay = new Date(endDate.getTime() + oneDay);
  const request = createRequest('POST', credentials, JSON.stringify({templateIds, startDate, endDate: finalDay}));
  const response = await fetch(`${getApiUrl()}/export/template`, request);

  if(!response.ok || response.status === 204) {
    throw response;
  }

  return await response;
}

export async function exportSingleTemplateData(templateId, startDate, endDate, credentials) {
  const response = await exportTemplateData([templateId], startDate, endDate, credentials);
  return await response.blob();
}

export async function getSheet(id, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/sheet/${id}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getSheets(templateId, page, size, sort, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/template/${templateId}/sheets?page=${page}&size=${size}&sort=${sort}`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getAllSheets(templateId, credentials, cancelController = null) {
  const CHUNK_SIZE = 100;

  // We needed to load the first response to know how many pages there are. Now we'll load the rest of them.
  const firstPage = await cancellable(getSheets(templateId, 0, CHUNK_SIZE, 'dates.lastModifiedDate,desc', credentials), cancelController);

  // Load the rest of the pages serially.
  for (let page = 1; page < firstPage.totalPages; page++) {
    const nextPage = await cancellable(getSheets(templateId, page, CHUNK_SIZE, 'dates.lastModifiedDate,desc', credentials), cancelController);
    firstPage.content = firstPage.content.concat(nextPage.content);
  }

  return firstPage.content;
}

export async function getPageRowsForSheets(sheets, filters, page, credentials) {
  let response;
  const SORT = 'dates.lastModifiedDate,desc';
  const CHUNK_SIZE = 500;
  let body = {
    sheetIds: sheets.map(sheet => sheet.id),
    rowFilters: {}
  }

  if (filters) {
    body.rowFilters = {
      ...filters,
      reviewState: filters.reviewState === '' ? null : filters.reviewState
    };
  }

  const request = createRequest('POST', credentials, JSON.stringify(body));
  response = await fetch(`${getApiUrl()}/row/filter?page=${page}&size=${CHUNK_SIZE}&sort=${SORT}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getSheetRows(id, filters, page, size, sort, credentials) {
  let response;

  if (filters) {
    filters = {
      ...filters,
      reviewState: filters.reviewState === '' ? null : filters.reviewState
    };
    
    const request = createRequest('POST', credentials, JSON.stringify(filters));
    response = await fetch(`${getApiUrl()}/sheet/${id}/row/filter?page=${page}&size=${size}&sort=${sort}`, request);
  }
  else {
    const request = createRequest('GET', credentials);
    response = await fetch(`${getApiUrl()}/sheet/${id}/row?page=${page}&size=${size}&sort=${sort}`, request);
  }

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getAllSheetRows(sheet, filters, credentials, cancelController) {
  const CHUNK_SIZE = 500;
  
  // Get the first page of rows.
  const firstPage = await cancellable(getSheetRows(sheet.id, filters, 0, CHUNK_SIZE, 'dates.lastModifiedDate,desc', credentials), cancelController);

  // Load the rest of the pages now that we know how many there are.
  for (let page = 1; page < firstPage.totalPages; page++) {
    const nextPage = await cancellable(getSheetRows(sheet.id, filters, page, CHUNK_SIZE, 'dates.lastModifiedDate,desc', credentials), cancelController);
    firstPage.content = firstPage.content.concat(nextPage.content);
  }

  return firstPage.content;
}

export async function createCatalogItem(item, credentials) {
  const body = JSON.stringify({
    name: item.name,
    parentId: item.parentId,
    typeId: item.type.id
  });
  const request = createRequest('POST', credentials, body);
  const response = await fetch(`${getApiUrl()}/catalog`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function editCatalogItem(item, credentials) {
  const body = JSON.stringify({
    name: item.name,
    parentId: item.parentId,
    typeId: item.type.id
  });
  const request = createRequest('PUT', credentials, body);
  const response = await fetch(`${getApiUrl()}/catalog/${item.id}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteCatalogItem(item, credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/catalog/${item.id}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function organizeCatalog(catalog, credentials) {
  const body = JSON.stringify({
    relationships: catalog.reduce((map, item) => {
      map[item.id] = item.parentId;
      return map;
    }, {})
  });
  const request = createRequest('PUT', credentials, body);
  const response = await fetch(`${getApiUrl()}/catalog/organize`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createCatalogType(type, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify(type));
  const response = await fetch(`${getApiUrl()}/catalog/type`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function editCatalogType(type, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(type));
  const response = await fetch(`${getApiUrl()}/catalog/type/${type.id}`, request);

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

async function exportSheetData(sheetIds, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify({sheetIds}));
  const response = await fetch(`${getApiUrl()}/export/sheet`, request);

  if(!response.ok || response.status === 204) {
    throw response;
  }

  return await response;
}

export async function exportMultiSheetData(sheetIds, credentials) {
  const response = await exportSheetData(sheetIds, credentials);
  return await response.blob();
}

export async function uploadImage(id, file, credentials) {
  const request = createRequest('POST', credentials, file, {
    'Content-Type': 'image/jpeg'
  });
  const response = await fetch(`${getApiUrl()}/image/${id}`, request);

  if(!response.ok) {
    throw response;
  }

  return response;
}

export async function editAnswers(sheetId, rowId, questionId, answers, comment, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({questionId, rowId, answers, comment}));
  const response = await fetch(`${getApiUrl()}/sheet/${sheetId}/answers`, request);

  if (!response.ok) {
    throw response;
  }
  event('Answer', 'Modified', 'Answer Edited');

  return response;
}

export async function getCompanyReminders(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/company/reminders`, request)

  if(!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createReminder(reminder, credentials) {
  const requestObj = {...reminder, groups: reminder.groups.reduce((list, item) => {
    list.push(item.id);
    return list;
  },[])}
  const request = createRequest('POST', credentials, JSON.stringify(requestObj));
  const response = await fetch(`${getApiUrl()}/reminder`, request);

  if(!response.ok) {
    throw response;
  }
  event('Reminder', 'Create', 'Reminder Created');

  return response.json();
}

export async function updateReminder(reminder, credentials) {
  const requestObj = {...reminder, groups: reminder.groups.reduce((list, item) => {
    list.push(item.id);
    return list;
  },[])}
  const request = createRequest('PUT', credentials, JSON.stringify(requestObj));
  const response = await fetch(`${getApiUrl()}/reminder/${reminder.id}`, request);

  if(!response.ok) {
    throw response;
  }
  event('Reminder', 'Modify', 'Reminder Edited');

  return response.json();
}

export async function deleteReminder(reminder, credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/reminder/${reminder.id}`, request);

  if(!response.ok) {
    throw response;
  }
}

export async function saveUserSetting(userId, key, value, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify({key, value}));
  const response = await fetch(`${getApiUrl()}/user/settings`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function createWidget(templateId, widget, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify(widget));
  const response = await fetch(`${getApiUrl()}/template/${templateId}/widget`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function editWidget(templateId, widget, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(widget));
  const response = await fetch(`${getApiUrl()}/template/${templateId}/widget/${widget.id}`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteWidget(templateId, widgetId, credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/template/${templateId}/widget/${widgetId}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function hideWidget(templateId, widgetToHide, credentials) {
  let request;
  let response;

  switch(widgetToHide.source) {
    case 'Context':
      request = createRequest('PUT', credentials, JSON.stringify(widgetToHide.widget));
      response = await fetch(`${getApiUrl()}/template/${templateId}/hidecontext/`, request);
      break;
    case 'TemplateWidget':
      request = createRequest('PUT', credentials, JSON.stringify(widgetToHide.widget));
      response = await fetch(`${getApiUrl()}/template/${templateId}/widget/${widgetToHide.widget.id}`, request);
      break;
    case 'Question':
      request = createRequest('PUT', credentials, JSON.stringify(widgetToHide.widget));
      response = await fetch(`${getApiUrl()}/question/${widgetToHide.widget.id}`, request);
      break;
    default:
      request = createRequest('PUT', credentials, JSON.stringify(widgetToHide.widget));
      response = await fetch(`${getApiUrl()}/question/${widgetToHide.widget.id}`, request);
  }

  if (!response.ok) {
    throw response;
  }
}

  export async function pinWidget(widgetToPin, credentials, uuids, filter) {
    let pinOptions = JSON.stringify({
      "uuids": uuids,
      "filter": JSON.stringify(filter),
      "step": widgetToPin.step ? JSON.stringify(widgetToPin.step) : 10
    })

    const request = createRequest('POST', credentials, pinOptions);
    const response = await fetch(`${getApiUrl()}/user/pinnedWidgets/pin/${widgetToPin.widget.id}/1`, request);

    if (!response.ok && response.status !== 429) {
      throw response;
    }
    return response.status;
}

  export async function unPinWidget(widgetToHide, credentials) {
    const request = createRequest('POST', credentials, "");
    const response = await fetch(`${getApiUrl()}/user/pinnedWidgets/unpin/${widgetToHide.id}`, request);

    if (!response.ok) {
      throw response;
    }
  }

  export async function updatePinnedWidget(widgetId, step, credentials) {
    let updatePinnedWidgetRequest = JSON.stringify({
      "step": step,
    })
    const request = createRequest('PUT', credentials, updatePinnedWidgetRequest);
    const response = await fetch(`${getApiUrl()}/user/pinnedWidget/${widgetId}`, request);

    if (!response.ok) {
      throw response;
    }
  }

export async function unhideWidgets(templateId, uuids, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({uuids}));
  const response = await fetch(`${getApiUrl()}/template/${templateId}/widgets/unhide`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function sortWidgets(templateId, requestBody, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(requestBody));
  const response = await fetch(`${getApiUrl()}/template/${templateId}/widgets/reorder`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function sortPinnedWidgets(userId, requestBody, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(requestBody));
  const response = await fetch(`${getApiUrl()}/user/${userId}/pinnedwidgets/reorder`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getAuditLogs(tags, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/audit_log?${tags.map(tag => `tag=${tag}`).join('&')}`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function filterAuditLogs(filters, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify(filters));
  const response = await fetch(`${getApiUrl()}/audit_log/filter`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function filterDeletedRowsAuditLogs(filters, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify(filters));
  const response = await fetch(`${getApiUrl()}/audit_log/deletedRows`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getTimeZones() {
  const response = await fetch(`${getApiUrl()}/timezone`, createRequest('GET'));

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getScheduledReports(reportId, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/report/${reportId}/schedule`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function createScheduleReport(reportId, requestBody, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify(requestBody));
  const response = await fetch(`${getApiUrl()}/report/${reportId}/schedule`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function editScheduleReport(reportId, jobId, requestBody, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(requestBody));
  const response = await fetch(`${getApiUrl()}/report/${reportId}/schedule/${jobId}`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteScheduledReport(reportId, jobId, credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/report/${reportId}/schedule/${jobId}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function getAccessToken(credentials) {
  const request = createRequest('POST', credentials);
  const response = await fetch(`${getApiUrl()}/access_token`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getBulkDownloadImagesUrl(templateId, questionId, sheetIds, credentials) {
  const {accessToken} = await getAccessToken(credentials);
  return `${getApiUrl()}/template/${templateId}/images?questionId=${questionId}&sheetIds=${sheetIds.join(',')}&accessToken=${accessToken}`;
}

export async function getNotifications(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/notification`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getPinnedWidgets(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/user/pinnedWidget`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function markNotificationAsRead(notificationId, credentials) {
  const request = createRequest('PUT', credentials);
  const response = await fetch(`${getApiUrl()}/notification/${notificationId}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function countUnreadNotifications(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/notification/unread_count`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function reviewSheetRow(sheetId, rowId, state, comment, credentials) {
  const request = createRequest('POST', credentials, JSON.stringify({state, comment}));
  const response = await fetch(`${getApiUrl()}/sheet/${sheetId}/row/${rowId}/review`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function uploadFile(file, mimetype, credentials, onSuccess, onFailure) {
  const uploadParams = await getImportUploadDetails(credentials);
  const reader =  new FileReader();

  reader.onload = async () => {
    const s3Request = createRequest(uploadParams.action, null, reader.result, {
      'Content-Type': mimetype
    });

    const response = await fetch(uploadParams.url, s3Request);

    if (response.ok) {
      onSuccess(uploadParams.s3Resource);
    }
    else {
      onFailure(response);
    }
  }

  reader.readAsText(file);
}

async function getImportUploadDetails(credentials) {
  const request = createRequest("GET", credentials);
  const response = await fetch(`${getApiUrl()}/upload/request`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function scheduleImport(form, fileName, originalFileName, dateColumn, dateFormat, totalRows, columnMap, credentials) {
  const body = {fileName, dateColumn, dateFormat, originalFileName, totalRows, columnMap};
  const request = createRequest("PUT", credentials, JSON.stringify(body));
  const response = await fetch(`${getApiUrl()}/template/${form.id}/import`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteImport(importId, credentials) {
  const request = createRequest("DELETE", credentials);
  const response = await fetch(`${getApiUrl()}/import/${importId}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function getFailedImport(importId, credentials) {
  const request = createRequest("GET", credentials);
  const response = await fetch(`${getApiUrl()}/import/${importId}`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function completeImport(importId, importRows, credentials) {
  const request = createRequest("PUT", credentials, JSON.stringify(importRows));
  const response = await fetch(`${getApiUrl()}/import/${importId}/complete`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function getImportStatus(importId, credentials) {
  const request = createRequest("GET", credentials);
  const response = await fetch(`${getApiUrl()}/import/${importId}/status`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getSupportedDateFormats(credentials) {
  const request = createRequest("GET", credentials);
  const response = await fetch(`${getApiUrl()}/import/date/formats`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getScheduledTemplateExports(form, credentials) {
  const request = createRequest("GET", credentials);
  const response = await fetch(`${getApiUrl()}/export/template/${form.id}/schedule/`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function saveTemplateExportSchedule(form, schedule, credentials) {
  const body = {...schedule, templateIds: [form.id]};
  let response;
  if (schedule.jobId === null) {
    const request = createRequest("POST", credentials, JSON.stringify(body));
    response = await fetch(`${getApiUrl()}/export/template/schedule`, request);
  }
  else {
    const request = createRequest("PUT", credentials, JSON.stringify(body));
    response = await fetch(`${getApiUrl()}/export/template/schedule/${schedule.jobId}`, request);
  }

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteTemplateExportSchedule(form, schedule, credentials) {
  const request = createRequest("DELETE", credentials);
  const response = await fetch(`${getApiUrl()}/export/template/${form.id}/schedule/${schedule.jobId}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function resetPassword(username) {
  const body = JSON.stringify({username});
  const request = createRequest('POST', null, body);
  const zones = multiAz.getAllZones();
  await Promise.all(zones.map(url => fetch(`${url}/user/resetPassword`, request)));
}

export async function getAlertInsights(startDate, endDate, credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/insights/alerts?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&size=1000`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function saveLabels(labels, credentials) {
  const body = JSON.stringify(labels);
  const request = createRequest('POST', credentials, body);
  const response = await fetch(`${getApiUrl()}/company/labels`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function getLabelLinks(credentials) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/company/labels`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function getLabels(link) {
  const request = createRequest('GET');
  const response = await fetch(link, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function deleteLabels(credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/company/labels`, request);

  if (!response.ok) {
    throw response;
  }

  return true;
}

async function getLogoUploadUrl(credentials, mimetype) {
  const request = createRequest('POST', credentials, JSON.stringify({ mimetype }));
  const response = await fetch(`${getApiUrl()}/company/logo`, request);

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

export async function uploadCompanyLogo(credentials, file, onSuccess, onFailure) {
  const { destination } = await getLogoUploadUrl(credentials, file.type);
  const reader = new FileReader();

  reader.onload = async () => {
    const s3Request = createRequest('PUT', null, reader.result, {
      'Content-Type': file.type,
      'x-amz-content-sha256': 'UNSIGNED-PAYLOAD'
    });

    const response = await fetch(destination, s3Request);
    if (response.ok) {
      const links = await getLabelLinks(credentials);
      onSuccess(links.logo);
    }
    else {
      onFailure();
    }
  }
  reader.readAsArrayBuffer(file);
}

export async function getCompanyLogoStatus(logoLink) {
  const response = await fetch(logoLink);

  return response.status;
}

export async function deleteCompanyLogo(credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/company/logo`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function deleteSheetRow(sheetId, rowId, credentials) { 
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/sheet/${sheetId}/row/${rowId}`, request);

  if (!response.ok) {
    throw response;
  }
}

export async function getTags(credentials, type) {
  const request = createRequest('GET', credentials);
  const response = await fetch(`${getApiUrl()}/tags?type=${type.toUpperCase()}`, request);
  
  if (!response.ok) {
    throw response;
  }
  
  return await response.json();
}

export async function getFormTags(credentials) {
  return getTags(credentials, 'FORM');
}

export async function getUserTags(credentials) {
  return getTags(credentials, 'USER');
}

export async function createTag(tagData, credentials, type) {
  const request = createRequest('POST', credentials, JSON.stringify({...tagData, type: type.toUpperCase()}));
  const response = await fetch(`${getApiUrl()}/tags`, request);
  
  if (!response.ok) {
    throw response;
  }
  
  return await response.json();
}

export async function createFormTag(tagData, credentials) {
  return createTag(tagData, credentials, 'FORM');
}

export async function createUserTag(tagData, credentials) {
  return createTag(tagData, credentials, 'USER');
}

export async function updateTag(tagId, updates, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify(updates));
  const response = await fetch(`${getApiUrl()}/tags/${tagId}`, request);
  
  if (!response.ok) {
    throw response;
  }
  
  return await response.json();
}

export async function deleteTag(tagId, credentials) {
  const request = createRequest('DELETE', credentials);
  const response = await fetch(`${getApiUrl()}/tags/${tagId}`, request);
  
  if (!response.ok) {
    throw response;
  }
}

export async function updateFormTags(formId, tagNames, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({tags: tagNames}));
  const response = await fetch(`${getApiUrl()}/template/${formId}/tags`, request);
  
  if (!response.ok) {
    throw response;
  }
  
  return await response.json();
}

export async function updateUserTags(userId, tagNames, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({tags: tagNames}));
  const response = await fetch(`${getApiUrl()}/user/${userId}/tags`, request);
  
  if (!response.ok) {
    throw response;
  }
  
  return await response.json();
}

export async function updateReportTags(reportId, tagNames, credentials) {
  const request = createRequest('PUT', credentials, JSON.stringify({tags: tagNames}));
  const response = await fetch(`${getApiUrl()}/report/${reportId}/tags`, request);
  
  if (!response.ok) {
    throw response;
  }
  
  return await response.json();
}

function createRequest(method, credentials, body, headers = {}) {
  if(credentials && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${credentials}`;
  }

  return {
    method,
    body,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    }
  };
}

async function cancellable(promise, cancelController) {
  if (!cancelController) {
    return promise;
  }

  return cancelController.cancellable(promise);
}